import { notFound } from 'next/navigation';
import { getRequestConfig } from 'next-intl/server';

export const locales = ['en', 'de', 'fr', 'hu', 'nl'] as const;
export type Locale = (typeof locales)[number];

// Locales promoted to users: shown in the language switcher and advertised in hreflang/sitemap.
// `locales` (above) is the full ROUTING set; `nl` is now a fully promoted public locale
// (complete chrome + 46 blog posts) alongside the others.
export const PUBLIC_LOCALES = ['en', 'de', 'fr', 'hu', 'nl'] as const;

const NAMESPACES = [
    'common',
    'home',
    'blog',
    'forms',
    'legal',
    'seo',
    'faq',
    'pricing',
    'coverage',
    'layers',
    'verticals',
    'partners',
] as const;

const loadNamespace = async (loc: string, ns: string) => {
    try {
        return (await import(`./messages/${loc}/${ns}.json`)).default;
    } catch {
        return (await import(`./messages/en/${ns}.json`)).default; // per-namespace English fallback
    }
};

const loadMessages = async (loc: string) => {
    const entries = await Promise.all(NAMESPACES.map(async (ns) => [ns, await loadNamespace(loc, ns)] as const));
    return Object.fromEntries(entries);
};

export default getRequestConfig(async ({ requestLocale }) => {
    // Await the locale (next-intl v4 API)
    const locale = await requestLocale;

    // Validate locale
    if (!locale || !locales.includes(locale as Locale)) {
        notFound();
    }

    const messages = await loadMessages(locale);

    return {
        locale,
        messages,
        timeZone: 'Europe/Luxembourg',
        now: new Date(),
    };
});
